import { animate, style, transition, trigger } from '@angular/animations';
import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssetDto, AssetVersionDto, AssetVideoTypeDataDto, GalleryDto } from '@nusign/picflow-backend-types';
import Hls from 'hls.js';
import { Subject, takeUntil } from 'rxjs';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { fadeIf } from '../../../animations/fade.if.animation';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { AudioVolumeIndicatorComponent } from '../../../gallery/components/audio-volume-indicator/audio-volume-indicator.component';
import { sleep } from '../../../lib/sleep';
import { throttle } from '../../../lib/throttle';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { VideoHlsService } from '../../../services/video-hls.service';
import { VideoSettingsService } from '../../../services/video-settings.service';
import { VideoTimeService } from '../../../services/video-time.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { VideoTimeData } from '../video-viewer/video-viewer.component';

@Component({
  selector: 'app-audio-viewer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './audio-viewer.component.html',
  styleUrls: ['./audio-viewer.component.scss'],
  animations: [
    trigger('controls', fadeUpIf),
    trigger('timeline', fadeIf),
    trigger('videoContainer', [transition(':enter', [])]),
    trigger('thumbnail', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translate(-50%, calc(-100% + 20px)) translateZ(0) scale(0.8)' }),
        animate('0.2s ease', style({ opacity: 1, transform: 'translate(-50%, -100%) translateZ(0)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translate(-50%, -100%)' }),
        animate(
          '0.2s ease',
          style({ opacity: 0, transform: 'translate(-50%, calc(-100% + 20px)) translateZ(0) scale(0.8)' })
        ),
      ]),
    ]),
  ],
  imports: [
    AudioVolumeIndicatorComponent,
    ButtonDirective,
    SvgComponent,
    TooltipDirective,
    FormsModule,
    ContextMenuComponent,
    DecimalPipe,
    AssetDownloadButtonComponent,
    DataUnitsBigIntPipe,
  ],
})
export class AudioViewerComponent implements OnChanges, OnDestroy {
  private videoTimeService = inject(VideoTimeService);
  private videoHlsService = inject(VideoHlsService);
  private videoSettingsService = inject(VideoSettingsService);
  private analytics = inject(PICFLOW_ANALYTICS);

  @HostListener('window:keydown', ['$event'])
  async keyDownEvent(event: KeyboardEvent) {
    if (!this.handleKeyboardShortcuts()) return;
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    if (event.code === 'Space') {
      event.preventDefault();
      if (!this.active()) {
        await this.init();
        this.play();
      } else this.togglePlay();
      this.analytics.reportKeyboardShortcut('play-pause-audio', 'asset-page');
    }

    if (event.code === 'ArrowLeft' && this.active()) {
      event.preventDefault();
      event.stopPropagation();
      this.moveSeconds(-5);
      // this.handleSkipTimeIndicator('back');
    }

    if (event.code === 'ArrowRight' && this.active()) {
      event.preventDefault();
      event.stopPropagation();
      this.moveSeconds(5);
      // this.handleSkipTimeIndicator('forward');
    }

    if (event.code === 'KeyM') {
      event.preventDefault();
      this.toggleMute();
      this.analytics.reportKeyboardShortcut('toggle-mute', 'audio-viewer');
      // this.handleMuteIndicator();
    }

    if (event.code === 'KeyS') {
      event.preventDefault();
      // this.togglePlaybackSpeed();
      this.analytics.reportKeyboardShortcut('toggle-playback-speed', 'audio-viewer');
    }
  }

  hls: Hls;
  @ViewChild('audioPlayer') audioPlayer: ElementRef<HTMLAudioElement>;
  @ViewChild('pastPlayed') pastPlayed: ElementRef<HTMLDivElement>;
  private el = inject(ElementRef);
  asset = input<AssetDto>();
  assetVersion = input<AssetVersionDto>();
  handleKeyboardShortcuts = input<boolean>();
  showControls = input<boolean>();
  reportTimeChange = input<boolean>();
  active = input<boolean>();
  autoplay = input<boolean>();
  videoData = input<{
    duration?: number;
  }>();
  onTimeChange = output<VideoTimeData>();
  initted = false;
  speeds = [0.2, 0.5, 0.8, 1, 1.2, 1.5, 2];
  mouseDownOnTimeline = signal(false);
  currentHoverPosition = signal<number>(null);
  buffered = signal<{ start: number; end: number }[]>([]);
  status = signal<'idle' | 'loading' | 'playing'>('idle');
  volume = signal(1);
  speed = signal(1);
  backupVolume = signal(1);
  muted = signal(false);

  gallery = input<GalleryDto>();

  destroyed$ = new Subject<void>();

  allowDownload = computed(() => {
    const gallery = this.gallery();
    const asset = this.asset();
    if (!gallery || !asset) return false;
    if (gallery.permissions.can_edit) return true;
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  });

  versionSize = computed(() => {
    const version = this.assetVersion();
    const asset = this.asset();
    if (!version && !asset) return;
    return (version || asset.version_data).size;
  });

  private videoTimeSub = this.videoTimeService.onTimeChange.pipe(takeUntil(this.destroyed$)).subscribe(async data => {
    if (!data) return;
    if (data.type === 'frame-change') {
      if (!this.active) return;
      this.moveFrames(data.amount);
    }
    if (data.type === 'time-set') {
      if (!this.active) await this.init();
      if (!this.audioPlayer?.nativeElement.paused) this.pause();
      this.updateCurrentTime(data.amount);
    }
  });

  ngOnChanges(changes: SimpleChanges) {
    if (this.active()) {
      this.init();
    } else {
      this.pause();
    }

    if (changes.autoplay && this.initted) {
      if (this.autoplay()) {
        this.play();
      } else {
        this.pause();
      }
    }
  }

  async init() {
    if (this.initted) return;
    this.initted = true;
    const version = this.assetVersion();
    const uuid = version.uuid;
    const data = version.data as AssetVideoTypeDataDto;
    const duration = data.duration;

    this.hls = new Hls({ autoStartLoad: false, maxBufferSize: 0, maxBufferLength: 9, startPosition: 0 });
    const url = this.videoHlsService.getAudioHlsUrl(uuid, version.original_key, duration);
    await this.videoHlsService.loadHls(url, () => !this.hls);

    if (!Hls.isSupported()) return;

    if (!this.hls || !this.audioPlayer) return;
    this.hls.loadSource(url);
    this.hls.startLoad();
    this.hls.attachMedia(this.audioPlayer.nativeElement);
    this.sendTimeData();
    if (this.autoplay()) {
      this.play();
    }
  }

  mousedown(event: MouseEvent) {
    this.setCurrentTimeFromClick(event);
    this.mouseDownOnTimeline.set(true);
  }

  mousemove(event: MouseEvent) {
    event.stopPropagation();
    this.setCurrentTimeFromClick(event);
  }

  private setCurrentTimeFromClick(event: MouseEvent) {
    const target = event.target as HTMLDivElement;
    const percent = this.getPercentFromMouseEvent(event);
    const time = percent * this.getDuration();
    const elBox = target.getBoundingClientRect();
    this.currentHoverPosition.set(Math.min(Math.max(event.clientX - elBox.left, 0), elBox.width));

    if (event.buttons !== 1) return;
    this.updateCurrentTime(time);
  }

  private getPercentFromMouseEvent(event: MouseEvent) {
    const el = this.el.nativeElement;
    const { width, left } = el.getBoundingClientRect();
    const percent = (event.clientX - left) / width;
    return Math.max(Math.min(percent, 1), 0);
  }

  private getDuration() {
    const version = this.assetVersion();
    if (!version) return 0;
    if (version.status === 'completed') {
      return this.audioPlayer?.nativeElement?.duration || 0;
    }
    const data = version.data as AssetVideoTypeDataDto;
    return data?.duration || 0;
  }

  public updateCurrentTime(time: number) {
    if (!this.audioPlayer?.nativeElement) return;
    if (time === Infinity || time === -Infinity || isNaN(time)) return;
    this.audioPlayer.nativeElement.currentTime = time;
    this.updateTimelineWidthActual();
  }

  private updateTimelineWidthActual() {
    const audio = this.audioPlayer?.nativeElement;
    if (this.pastPlayed && audio) {
      this.pastPlayed.nativeElement.style.width = this.getPastPlayedWidth() + '%';
    }
  }

  getPastPlayedWidth() {
    const audioEl = this.audioPlayer?.nativeElement;
    if (!audioEl) return 0;
    const duration = this.getDuration();
    if (!duration) return 0;
    return (audioEl.currentTime / duration) * 100;
  }

  getProcessingPieceWidth() {
    const version = this.assetVersion();
    if (!version) return 0;
    if (version.status === 'completed') return 0;
    const data = version.data as AssetVideoTypeDataDto;
    const fullDuration = data.duration || 0;
    const readyDuration = this.audioPlayer?.nativeElement?.duration || 0;
    return 100 - (readyDuration / fullDuration) * 100;
  }

  touchTimelineInteraction(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  playPauseClick(event: MouseEvent) {
    event.stopPropagation();
    this.togglePlay();
  }

  togglePlay() {
    if (this.audioPlayer.nativeElement.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  async play() {
    this.status.set('loading');
    try {
      await this.audioPlayer.nativeElement.play();
      this.status.set('playing');
    } catch (err) {
      console.debug(err);
      this.status.set('idle');
    }
  }

  pause(retry = false) {
    if (!this.audioPlayer && !retry) {
      setTimeout(() => this.pause(true), 10);
      return;
    }
    this.audioPlayer.nativeElement.pause();
    this.status.set('idle');
  }

  toggleMute() {
    if (this.muted()) {
      this.unmute();
    } else this.mute();
  }

  toggleMutePreventDefault(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.toggleMute();
  }

  mute() {
    this.muted.set(true);
    this.audioPlayer.nativeElement.muted = true;
    if (this.volume) this.backupVolume.set(this.volume());
    this.volume.set(0);
    this.audioPlayer.nativeElement.volume = 0;
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume(),
    });
  }

  unmute() {
    this.muted.set(false);
    this.audioPlayer.nativeElement.muted = false;
    this.volume.set(this.backupVolume());
    this.audioPlayer.nativeElement.volume = this.volume();
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume(),
    });
  }

  adjustVolume() {
    this.audioPlayer.nativeElement.volume = this.volume();
    if (this.volume() === 0 && !this.muted) this.mute();
    else if (this.muted) {
      this.backupVolume.set(this.volume());
      this.unmute();
    }
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume(),
    });
  }

  adjustSpeed(speed: number) {
    this.speed.set(speed);
    this.audioPlayer.nativeElement.playbackRate = this.speed();
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      speed: this.speed(),
    });
  }

  private moveSeconds(seconds: number) {
    if (!this.audioPlayer) return;
    const video = this.audioPlayer.nativeElement;
    video.currentTime += seconds;
    this.sendTimeData();
    this.updateTimelineWidthActual();
  }

  private sendTimeData = throttle(async () => {
    if (!this.reportTimeChange()) return;
    if (!this.hls) return;
    const audioEl = this.audioPlayer?.nativeElement;
    this.onTimeChange.emit({
      currentSecond: audioEl?.currentTime || 0,
      durationSeconds: this.videoData().duration || 0,
      currentFrame: undefined,
      durationFrames: undefined,
    });
    await sleep(100);
    this.sendTimeData();
  }, 99);

  moveFrames(frameCount: number) {
    if (!this.audioPlayer) return;
    const audio = this.audioPlayer.nativeElement;
    audio.currentTime += frameCount / 30;
    this.sendTimeData();
    this.updateTimelineWidthActual();
  }

  ngOnDestroy() {
    this.hls?.destroy();
    this.hls = undefined;
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
